import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import axios from "axios";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { createdByColumns } from "data/globalData";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import EditCatalogRowModal from "../shared/EditCatalogRowModal";
import { catalogsNeedingFormOptions } from "../shared/catalogFormHelpers";
import { withCatalogStatusColumn } from "../shared/catalogMasterUi";

const CatalogTabPanel = ({
	apiBase,
	catalog,
	columns = [],
	primaryKey,
	editableFields = [],
	creatableFields = [],
	autoPrimaryKey = false,
	getRowId,
	refreshKey = 0,
	onSummaryRefresh,
	formOptionsApiPath,
}) => {
	const { setLoadingOpen, snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const [data, setData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterObj, setFilterObj] = useState({});
	const [selectedRow, setSelectedRow] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState("update");
	const [localRefreshKey, setLocalRefreshKey] = useState(0);
	const [formOptions, setFormOptions] = useState({});
	const catalogRef = useRef(catalog);
	catalogRef.current = catalog;

	const needsFormOptions = catalogsNeedingFormOptions.has(catalog);

	const loadFormOptions = useCallback(async () => {
		if (!needsFormOptions || !formOptionsApiPath) return;
		try {
			const res = await axios.get(formOptionsApiPath);
			setFormOptions(res.data?.result ?? {});
		} catch {
			setFormOptions({});
		}
	}, [needsFormOptions, formOptionsApiPath]);

	useEffect(() => {
		if (modalOpen && needsFormOptions) {
			loadFormOptions();
		}
	}, [modalOpen, needsFormOptions, loadFormOptions]);

	const tableColumns = useMemo(
		() =>
			withCatalogStatusColumn([...columns, ...createdByColumns]).map((row) => ({
				...row,
				align: "center",
				headerAlign: "center",
			})),
		[columns],
	);

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			const res = await axios.post(`${apiBase}/get`, {
				catalog: catalogRef.current,
				limit,
				offset,
				filter,
			});
			return res.data.result;
		},
		[apiBase],
	);

	const handleAddClick = () => {
		setSelectedRow(null);
		setModalMode("create");
		setModalOpen(true);
	};

	const handleRowClick = ({ row }) => {
		setSelectedRow(row);
		setModalMode("update");
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
		setSelectedRow(null);
	};

	const handleSave = async (pageData) => {
		setLoadingOpen(true);
		try {
			if (modalMode === "create") {
				await axios.post(`${apiBase}/create`, {
					catalog,
					...pageData,
				});
				setLocalRefreshKey((prev) => prev + 1);
				onSummaryRefresh?.();
				setModalOpen(false);
				setSelectedRow(null);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "success",
					message: "Catalog row created successfully",
				});
				return;
			}

			const res = await axios.put(`${apiBase}/update`, {
				catalog,
				...pageData,
			});
			const updatedRow = res.data?.result ?? pageData;
			setData((prev) =>
				prev.map((row) => (getRowId(catalog, row) === getRowId(catalog, updatedRow) ? { ...row, ...updatedRow } : row)),
			);
			setModalOpen(false);
			setSelectedRow(null);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "success",
				message: "Catalog row updated successfully",
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }} key={`${catalog}-${refreshKey}-${localRefreshKey}`}>
			<TanstackDatagrid
				columns={tableColumns}
				pageData={data}
				setPageData={setData}
				rowCount={rowCount}
				setRowCount={setRowCount}
				filterObj={filterObj}
				setFilterObj={setFilterObj}
				isLoading={false}
				emptyMessage="No matching records"
				handleRowClick={handleRowClick}
				getServerSideData={fetchData}
				getRowId={(row) => getRowId(catalog, row)}>
				<Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
					<Button variant="contained" size="small" startIcon={<AddRounded />} onClick={handleAddClick}>
						Add
					</Button>
				</Box>
			</TanstackDatagrid>
			<EditCatalogRowModal
				open={modalOpen}
				onClose={handleModalClose}
				mode={modalMode}
				catalog={catalog}
				columns={columns}
				primaryKey={primaryKey}
				editableFields={editableFields}
				creatableFields={creatableFields}
				autoPrimaryKey={autoPrimaryKey}
				selectedRow={selectedRow}
				formOptions={formOptions}
				onSave={handleSave}
			/>
		</Box>
	);
};

export default CatalogTabPanel;
